import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchEntityTranslatedData, supportedLanguages } from "../utils/RequestHandler";
import {
  getFormattedTripDate,
  sortItineraryActivities,
} from "../utils/DataHandler";
import EmptyStateView from "../components/Elements/EmptyStateView";
import LoadingSpinner from "../components/Elements/LoadingSpinner";
import { getEmergencyContacts } from "../data/emergency.contacts";
import {
  ContentContainer,
  ExportButton,
} from "../style/booking.details.styles";
import HeaderSectionView from "../components/Sections/BookingDetails/HeaderSectionView";
import BodySectionView from "../components/Sections/BookingDetails/BodySectionView";
import FooterSectionView from "../components/Sections/BookingDetails/FooterSection";
import { useTranslation } from "react-i18next";

export default function BookingDetails() {
  const [loading, setLoading] = useState(true);
  const [itinerary, setItinerary] = useState(null);
  const [itiniraryActivities, setItineraryActivities] = useState([]);
  const [bookingData, setBookingData] = useState(null);
  const [activitiesDetails, setActivitiesDetails] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [language, setLanguage] = useState(null);
  const { bookingCode } = useParams();
  const printRef = useRef();
  const navigate = useNavigate();

  const { t, i18n } = useTranslation("booking_details");

  useEffect(() => {
    const savedLang = localStorage.getItem("app_language");

    if (savedLang) {
      i18n.changeLanguage(savedLang);
      setLanguage(savedLang);
    } else {
      const defaultLang = i18n.language || window.navigator.language || "en";
      const finalLang = supportedLanguages.includes(defaultLang) ? defaultLang : "en";
      i18n.changeLanguage(finalLang);
      localStorage.setItem("app_language", finalLang);
      setLanguage(finalLang);
    }
    // eslint-disable-next-line
  }, []);

  const goHome = () => {
    navigate("/");
  };

  useEffect(() => {
    if (!language) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const bookingResponse = await fetchEntityTranslatedData(
          "bookings",
          language
        );
        if (!bookingResponse.success) return;
        const booking = bookingResponse.result?.find(
          (b) => b.booking_code === bookingCode
        );
        if (!booking) return;

        setBookingData(booking);

        const itineraryName = booking.itinerary;

        const [
          itineraryResponse,
          itineraryActivitiesResponse,
          activitiesResponse,
        ] = await Promise.all([
          fetchEntityTranslatedData("itineraries", language),
          fetchEntityTranslatedData("itinirary_activities", language),
          fetchEntityTranslatedData("activities", language),
        ]);

        if (itineraryResponse.success) {
          const itineraryData = itineraryResponse.result?.find(
            (item) => item.name === itineraryName
          );
          setItinerary(itineraryData);
        }

        if (itineraryActivitiesResponse.success) {
          const itineraryActivitiesData =
            itineraryActivitiesResponse.result?.filter(
              (item) => item.itinerary === itineraryName
            );
          setItineraryActivities(itineraryActivitiesData);
        }

        if (activitiesResponse.success) {
          setActivitiesDetails(activitiesResponse.result);
        }
      } catch (error) {
        console.error("Failed fetching booking or itinerary data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [bookingCode, language]);

  const activities = sortItineraryActivities(itinerary, itiniraryActivities);

  const renderedDays = [];

  const handleDownloadPDF = async () => {
    if (!bookingData || !itinerary) return;

    try {
      setIsPrinting(true);
      const emergencies = getEmergencyContacts(itinerary.country).map((emergencyContact)=>{
        return {
          ...emergencyContact,
          service: t(emergencyContact.service)
        }
      });
      const payload = {
        bookingData,
        itinerary,
        activities: itiniraryActivities,
        activitiesDetails,
        emergencyContacts: emergencies,
        language: language
      };

      const response = await fetch(
        `${process.env.REACT_APP_INTELLIGENCE_URL}/api/reports/generate-pdf/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) throw new Error("Failed to generate PDF");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "booking.pdf";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("PDF generation failed:", error);
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <>
      {loading && <LoadingSpinner />}
      {!loading && !bookingData && (
        <EmptyStateView message={"Invalid booking code"} />
      )}
      {!loading && bookingData && (
        <ContentContainer ref={printRef}>
          {/* Header Section */}
          <HeaderSectionView
            itinerary={itinerary}
            goHome={goHome}
            bookingData={bookingData}
            id="header-section"
          />
          {/* Body Section - Itinerary */}
          <BodySectionView
            itinerary={itinerary}
            activities={activities}
            activitiesDetails={activitiesDetails}
            renderedDays={renderedDays}
            getFormattedTripDate={getFormattedTripDate}
            bookingData={bookingData}
            id="body-section"
            language={language}
          />

          {/* Footer Section */}
          <FooterSectionView
            itinerary={itinerary}
            getEmergencyContacts={getEmergencyContacts}
            id="footer-section"
          />
          <ExportButton
            className="no-pdf"
            onClick={handleDownloadPDF}
            disabled={isPrinting}
          >
            {isPrinting ? `${t("exporting")}...` : t("export_button")}
          </ExportButton>
        </ContentContainer>
      )}
    </>
  );
}
